import { createSlice } from '@reduxjs/toolkit';

import api from 'services/api';
import Poller from 'utils/poller';

import flash from './flash';

// Active runs are read this often, while the page is visible
export const RUN_POLL_INTERVAL = 3000;

// The most active runs the workflows API lists at once
export const ACTIVE_RUNS_LIMIT = 100;

// Codes of a run that cannot be read anymore: the run, its workflow or its
// app is gone
const RUN_GONE_CODES = [
  'workflow_instance_not_found',
  'workflow_not_found',
  'app_install_missing',
  'app_uninstalled',
  'app_inactive',
];

/**
 * Workflow runs started by app actions, one per action (and record), so each
 * action shows its latest run. Actions of an app are told apart by id alone,
 * so actions that share an id in several places (such as an Actions menu
 * action and an action field) share their runs.
 */
export const initialState = {};

const slice = createSlice({
  name: 'workflowRuns',
  initialState,
  reducers: {
    setRun(state, { payload }) {
      state[getRunKey(payload.app_id, payload.action_id, payload.record_id)] =
        payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('RESET', () => initialState);
  },
});

export const { reducer } = slice;

// What to refresh when a run finishes, by run id
const runCallbacks = new Map();

// Active runs of an app (on a record, of an action) being read, so the restores
// of a page share one request
const activeRunRequests = new Map();

// One poll loop reads every active run
let poller = null;

/**
 * @param {string} appId
 * @param {string} actionId
 * @param {string} [recordId]
 * @returns {string}
 */
export function getRunKey(appId, actionId, recordId) {
  return [appId, actionId, recordId].filter(Boolean).join(':');
}

/**
 * The latest run of an action (on a record)
 * @param {object} runs - workflowRuns state
 * @param {string} appId
 * @param {string} actionId
 * @param {string} [recordId]
 * @returns {object|undefined}
 */
export function getActionRun(runs, appId, actionId, recordId) {
  return runs[getRunKey(appId, actionId, recordId)];
}

function getActiveRuns(state) {
  return Object.values(state.workflowRuns).filter(
    (run) => run.status === 'active',
  );
}

// Only the page that shows the run is refreshed when it finishes
function setRunCallback(runId, onComplete) {
  runCallbacks.set(runId, {
    onComplete,
    pathname: window.location.pathname,
  });
}

/**
 * Active runs of an app, on a record or on any, of an action or of any,
 * latest first
 * @param {string} appId
 * @param {string} [recordId]
 * @param {string} [actionId]
 * @returns {Promise<object>}
 */
function fetchActiveRuns(appId, recordId, actionId) {
  const key = [appId, recordId, actionId].join(':');

  if (!activeRunRequests.has(key)) {
    activeRunRequests.set(
      key,
      api
        .get('/data/:workflows/instances', {
          app_id: appId,
          action_id: actionId,
          record_id: recordId,
          status: 'active',
          limit: ACTIVE_RUNS_LIMIT,
        })
        .finally(() => activeRunRequests.delete(key)),
    );
  }

  return activeRunRequests.get(key);
}

/**
 * Active runs of an app's actions, on a record or on any. One action reads
 * its own runs. Several read the runs of the app together, and each their own
 * when the app has more than the limit.
 * @param {string} appId
 * @param {Array<object>} appActions
 * @param {string} [recordId]
 * @returns {Promise<Array<object>>}
 */
async function fetchActionRuns(appId, appActions, recordId) {
  if (appActions.length > 1) {
    const result = await fetchActiveRuns(appId, recordId);

    if (!result?.has_more) {
      return result?.results || [];
    }
  }

  const results = await Promise.all(
    appActions.map((action) => fetchActiveRuns(appId, recordId, action.id)),
  );

  return results.flatMap((result) => result?.results || []);
}

/**
 * Code of an API error, as the backend sent it
 * @param {Error} err
 * @returns {string|undefined}
 */
export function getErrorCode(err) {
  try {
    return JSON.parse(err.response.data).error.code;
  } catch (parseError) {
    return undefined;
  }
}

// Run fields as the workflows API returns them
function getRunFields(run) {
  return {
    status: run.status,
    current_step: run.current_step,
    error: run.error?.message,
    date_created: run.date_created,
  };
}

function stopPolling() {
  poller?.stop();
  poller = null;
  runCallbacks.clear();
}

const actions = {
  /**
   * Tracks a run an app action started, until it finishes
   * @param {object} options
   * @param {object} options.app
   * @param {object} options.action - with id and label
   * @param {string} [options.recordId]
   * @param {object} options.run - {id, status} from run_action
   * @param {Function} [options.onComplete] - refreshes the page after the run
   */
  start({ app, action, recordId, run, onComplete }) {
    return actions.track(
      {
        id: run.id,
        app_id: app.id,
        action_id: action.id,
        record_id: recordId,
        label: action.label,
        status: run.status || 'active',
        date_created: new Date().toISOString(),
      },
      onComplete,
    );
  },

  /**
   * Tracks the active runs of an app's actions started before the page
   * loaded, or by another user. Runs already tracked refresh this page when
   * they finish.
   * @param {object} options
   * @param {object} options.app
   * @param {Array<object>} options.actions - actions of the app, with id and
   * label
   * @param {string} [options.recordId]
   * @param {Function} [options.onComplete]
   */
  restore({ app, actions: appActions, recordId, onComplete }) {
    return async (dispatch, getState) => {
      const getRun = (action) =>
        getActionRun(getState().workflowRuns, app.id, action.id, recordId);

      const untracked = [];

      for (const action of appActions) {
        const run = getRun(action);

        // A run tracked before this page opened refreshes this page instead
        if (run?.status === 'active') {
          setRunCallback(run.id, onComplete);
        } else {
          untracked.push(action);
        }
      }

      if (!untracked.length) {
        return;
      }

      let activeRuns;

      try {
        activeRuns = await fetchActionRuns(app.id, untracked, recordId);
      } catch (err) {
        // The status is only shown when it can be read
        return;
      }

      for (const action of untracked) {
        // Runs of the action on another record, or on none, are not its own.
        // A run already seen to end is not tracked again, such as one still
        // listed as active after its workflow was removed.
        const run = activeRuns.find(
          (run) =>
            run.action?.id === action.id &&
            (run.action.record_id || null) === (recordId || null) &&
            run.workflow_instance_id !== getRun(action)?.id,
        );

        if (run && getRun(action)?.status !== 'active') {
          dispatch(
            actions.track(
              {
                id: run.workflow_instance_id,
                app_id: app.id,
                action_id: action.id,
                record_id: recordId,
                label: action.label,
                ...getRunFields(run),
              },
              onComplete,
            ),
          );
        }
      }
    };
  },

  track(run, onComplete) {
    return (dispatch) => {
      dispatch(slice.actions.setRun(run));
      setRunCallback(run.id, onComplete);
      dispatch(actions.watch());
    };
  },

  // Polls while any run is active, and again as soon as the page is visible
  watch() {
    return (dispatch, getState) => {
      if (!getActiveRuns(getState()).length) {
        stopPolling();
        return;
      }

      // Paused while the page is hidden
      if (!poller) {
        poller = new Poller(() => dispatch(actions.poll()), {
          interval: RUN_POLL_INTERVAL,
        });
        poller.start();
      }
    };
  },

  poll() {
    return async (dispatch, getState) => {
      await Promise.all(
        getActiveRuns(getState()).map((run) => dispatch(actions.refresh(run))),
      );

      dispatch(actions.watch());
    };
  },

  refresh(run) {
    return async (dispatch) => {
      let result;

      try {
        result = await api.get(`/data/:workflows/runs/${run.id}`, {
          app_id: run.app_id,
        });
      } catch (err) {
        // A run that can no longer be read is not watched anymore. Other
        // errors, which the admin API answers with 400 too (such as while the
        // backend restarts), are read again.
        if (RUN_GONE_CODES.includes(getErrorCode(err))) {
          await dispatch(
            actions.finish(run, { status: 'failed', error: err.message }),
          );
        }
        return;
      }

      if (!result?.run) {
        return;
      }

      const fields = getRunFields(result.run);

      if (fields.status === 'active') {
        dispatch(actions.update(run, fields));
      } else {
        await dispatch(actions.finish(run, fields));
      }
    };
  },

  update(run, fields) {
    return (dispatch, getState) => {
      const current = getActionRun(
        getState().workflowRuns,
        run.app_id,
        run.action_id,
        run.record_id,
      );

      // Another run of the action may have replaced it, and an unchanged
      // run is not set again
      if (
        current?.id === run.id &&
        Object.keys(fields).some((key) => current[key] !== fields[key])
      ) {
        dispatch(slice.actions.setRun({ ...current, ...fields }));
      }
    };
  },

  finish(run, fields) {
    return async (dispatch) => {
      dispatch(actions.update(run, fields));

      const { label } = run;

      if (fields.status === 'completed') {
        dispatch(flash.success(`"${label}" finished`));
      } else if (fields.status === 'terminated') {
        dispatch(flash.warning(`"${label}" was canceled`));
      } else {
        dispatch(
          flash.error(
            fields.error
              ? `"${label}" failed: ${fields.error}`
              : `"${label}" failed`,
          ),
        );
      }

      const callback = runCallbacks.get(run.id);
      runCallbacks.delete(run.id);

      if (
        callback?.onComplete &&
        callback.pathname === window.location.pathname
      ) {
        try {
          await callback.onComplete();
        } catch (err) {
          console.error(err);
        }
      }
    };
  },
};

export default actions;
